import type { BusinessInfo } from '@/lib/types';
import { useNavigate } from 'react-router-dom';

export interface BranchDirectoryItem {
  name: string;
  phone: string | null;
  whatsapp: string | null;
  logo_url: string | null;
  instagram_url?: string | null;
  facebook_url?: string | null;
  tiktok_url?: string | null;
}

export interface SocialLinks {
  instagram_url?: string | null;
  facebook_url?: string | null;
  tiktok_url?: string | null;
}

interface CatalogHeaderProps {
  info: BusinessInfo | null;
  branches: BranchDirectoryItem[];
  social: SocialLinks;
}

interface SocialItem {
  key: string;
  icon: string;
  label: string;
  url: string;
}

function normalizeSocial(value: string | null | undefined, base: string): string | null {
  const raw = (value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const handle = raw.replace(/^@/, '');
  return `${base}${handle}`;
}

function buildSocialItems(source: SocialLinks): SocialItem[] {
  return [
    {
      key: 'instagram',
      icon: 'ri-instagram-line',
      label: 'Instagram',
      url: normalizeSocial(source.instagram_url, 'https://instagram.com/'),
    },
    {
      key: 'facebook',
      icon: 'ri-facebook-circle-line',
      label: 'Facebook',
      url: normalizeSocial(source.facebook_url, 'https://facebook.com/'),
    },
    {
      key: 'tiktok',
      icon: 'ri-tiktok-line',
      label: 'TikTok',
      url: normalizeSocial(source.tiktok_url, 'https://tiktok.com/@'),
    },
  ].filter((s): s is SocialItem => Boolean(s.url));
}

export default function CatalogHeader({ info, branches, social }: CatalogHeaderProps) {
  const navigate = useNavigate();

  const globalSocial = buildSocialItems(social);
  const hasBranchSocial = branches.some((b) => buildSocialItems(b).length > 0);

  const branchNames = branches.map((b) => b.name).filter(Boolean).join(' / ');
  const title = info?.name || branchNames || 'Mi Tienda';

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <header className="border-b border-background-200 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Botón atrás + nombre del negocio */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={goBack}
            aria-label="Volver atrás"
            title="Volver atrás"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition cursor-pointer shrink-0"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <span className="font-heading font-bold text-base md:text-lg text-foreground-950 truncate">
            {title}
          </span>
        </div>

        {/* Sucursales: redes sociales encima del nombre de cada sucursal */}
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3 sm:justify-end">
          {branches.map((b, index) => {
            const items = buildSocialItems(b);
            const num = (b.phone || b.whatsapp || '').trim();
            const tel = num.replace(/\D/g, '');
            return (
              <div
                key={`${b.name}-${index}`}
                className="flex flex-col items-start sm:items-end gap-1"
              >
                {items.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    {items.map((s) => (
                      <a
                        key={s.key}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${s.label} de ${b.name}`}
                        title={`${s.label} de ${b.name}`}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition cursor-pointer"
                      >
                        <i className={`${s.icon} text-sm`} />
                      </a>
                    ))}
                  </div>
                )}
                <span className="text-xs md:text-sm font-semibold text-foreground-950 whitespace-nowrap">
                  {b.name}
                </span>
                {tel && (
                  <a
                    href={`tel:${tel}`}
                    className="flex items-center gap-1 text-[11px] md:text-xs text-foreground-600 hover:text-foreground-950 transition whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-primary-700" />
                    {num}
                  </a>
                )}
              </div>
            );
          })}

          {/* Respaldo: si ninguna sucursal tiene redes propias, mostramos las generales del negocio */}
          {!hasBranchSocial && globalSocial.length > 0 && (
            <div className="flex items-center gap-1.5">
              {globalSocial.map((s) => (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition cursor-pointer"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}